<template>
    <div
        class="sales-button"
        :class="{ draggable: configurable && !imageDialogVisible }"
        @mouseover="showMenu = mode != 'edit'"
        @mouseleave="showMenu = false"
    >
        <!-- Settings Menu -->
        <div v-if="configurable" class="menu" :style="menuStyle">
            <div class="label">{{ config.product != null ? config.product.name : config.category.name }}</div>

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" text fab dark class="menu-item" @click="menuItemClick('edit')">
                        <v-icon size="16">fal fa-edit</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.edit_text') }}</span>
            </v-tooltip>

            <v-menu offset-y bottom :close-on-content-click="false">
                <template v-slot:activator="{ on: menu }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on: tooltip }">
                            <v-btn v-on="{ ...tooltip, ...menu }" text fab dark class="menu-item">
                                <v-icon size="16">fal fa-text-width</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('general.text_alignment') }}</span>
                    </v-tooltip>
                </template>

                <div class="sales-button__text-settings">
                    <div class="label">{{ $t('general.align_text') }}</div>
                    <div class="align">
                        <align-widget
                            class="text-align"
                            :init-align="{ horizontal: config.text_halign, vertical: config.text_valign }"
                            @update="alignText"
                        />
                    </div>
                </div>
            </v-menu>

            <v-menu v-model="showImageSettingsMenu" offset-y bottom :close-on-content-click="false">
                <template v-slot:activator="{ on: menu }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on: tooltip }">
                            <v-btn
                                v-on="{ ...tooltip, ...menu }"
                                class="menu-item"
                                text
                                fab
                                dark
                            >
                                <v-icon size="16">fal fa-file-image</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('general.image_settings') }}</span>
                    </v-tooltip>
                </template>

                <div class="sales-button__image-settings">
                    <div class="label">{{ $t('general.image_settings') }}</div>
                    <div class="align">
                        <div class="fit-stretch">
                            <v-btn
                                :color="config.image_fit ? 'info' : 'default'"
                                small
                                @click="fitImage"
                            >
                                {{ $t('general.fit') }}
                            </v-btn>
                            <v-btn
                                :color="config.image_stretch ? 'info' : 'default'"
                                small
                                class="mt-1"
                                @click="stretchImage"
                            >
                                {{ $t('general.stretch') }}
                            </v-btn>
                        </div>
                        <align-widget
                            class="image-align"
                            :init-align="{ horizontal: config.image_halign, vertical: config.image_valign }"
                            @update="alignImage"
                        />
                    </div>
                    <v-btn class="select-button" block @click="openImageDialog">{{ $t('general.select_image') }}</v-btn>
                </div>
            </v-menu>

            <v-menu offset-y bottom>
                <template v-slot:activator="{ on: menu }">
                    <v-tooltip top>
                        <template v-slot:activator="{ on: tooltip }">
                            <v-btn
                                v-on="{ ...tooltip, ...menu }"
                                class="menu-item"
                                text
                                fab
                                dark
                            >
                                <v-icon size="16">fal fa-palette</v-icon>
                            </v-btn>
                        </template>
                        <span>{{ $t('general.background_color') }}</span>
                    </v-tooltip>
                </template>

                <v-list class="sales-button__select-color">
                    <v-list-item
                        v-for="(color, index) in colors"
                        :key="index"
                        @click="menuItemClick('color', color)"
                    >
                        <v-list-item-title :style="`background: ${color};`" />
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-tooltip top>
                <template v-slot:activator="{ on }">
                    <v-btn
                        class="menu-item"
                        text
                        fab
                        dark
                        @click="menuItemClick('delete')"
                    >
                        <v-icon size="16">fal fa-times</v-icon>
                    </v-btn>
                </template>
                <span>{{ $t('general.delete') }}</span>
            </v-tooltip>
        </div>

        <!-- Settings - Select Image Dialog -->
        <v-dialog v-if="configurable" v-model="imageDialogVisible" width="555px">
            <div class="sales-button__button-image-dialog">
                <div class="header">
                    <h4>{{ $t('general.select_image') }}</h4>
                </div>

                <div class="content">
                    <v-btn
                        class="upload-btn"
                        text
                        @click="$refs.inputUpload.click()"
                    >
                        <v-icon size="16">fal fa-plus</v-icon>
                    </v-btn>
                    <input v-show="false" ref="inputUpload" type="file" @change="uploadImage" />

                    <div
                        v-for="(image, index) in images"
                        :key="index"
                        class="image-wrapper"
                        :class="{ selected: newImage && newImage.image_url === image.image_url }"
                        @click="newImage = image"
                    >
                        <img :src="image.image_url" class="image" />
                    </div>
                </div>

                <span class="footer">
                    <v-btn
                        class="mr-1 flex-1"
                        outlined
                        @click="imageDialogVisible = false"
                    >
                        {{ $t('general.cancel') }}
                    </v-btn>
                    <v-btn
                        class="ml-1 flex-1"
                        color="primary"
                        depressed
                        @click="updateImage"
                    >
                        {{ $t('general.confirm') }}
                    </v-btn>
                </span>
            </div>
        </v-dialog>

        <!-- Button Element -->
        <v-btn
            class="button-element"
            :style="style"
            :disabled="disabled"
            @click="$emit('click', config)"
        >
            <div class="text-wrapper" :style="contentStyle">
                <v-badge
                    v-if="badge"
                    color="error"
                    :content="badge"
                    :left="false"
                    :bottom="false"
                />
                <template v-if="mode != 'edit'">{{ config.text }}</template>
                <input
                    v-else
                    ref="editNameField"
                    v-model="config.text"
                    class="edit-name"
                    type="text"
                    @keyup.enter="finishEdit"
                >
            </div>
        </v-btn>
    </div>
</template>

<script>
import AlignWidget from '@/components/AlignWidget';
import ApiService from '@/services/ApiService';

export default {
    components: {
        AlignWidget
    },

    props: {
        buttonConfig: {
            type: Object,
            default: () => {}
        },
        configurable: {
            type: Boolean,
            default: false
        },
        selected: {
            type: Boolean,
            default: false
        },
        images: {
            type: Array,
            default: () => []
        },
        badge: {
            type: [String, Number],
            default: null
        },
        disabled: {
            type: Boolean,
            default: false
        },
    },

    data() {
        return {
            config: this.buttonConfig,
            mode: null,
            showMenu: false,
            imageDialogVisible: false,
            newImage: null,
            showImageSettingsMenu: false,
            colors: [ '#82b1ff', '#8f9398', '#f56c6c', '#67c23a', '#e6a23b', '#ffffff' ]
        };
    },

    watch: {
        buttonConfig: {
            deep: true,
            handler(val) {
                this.config = val;
            }
        }
    },

    computed: {
        style() {
            return {
                backgroundColor: this.config
                    ? !this.selected
                        ? this.config.background_color
                        : this.shadeColor(this.config.background_color, -30)
                    : null,
                color: this.config ? (this.config.background_color === '#ffffff' ? '#606266' : '#ffffff') : null,
            };
        },

        menuStyle() {
            return {
                display: this.showMenu && this.configurable ? 'block' : 'none'
            };
        },

        contentStyle() {
            if (!this.config) {
                return {};
            }

            return {
                alignItems:
                    this.config.text_valign === 'top'
                        ? 'flex-start'
                        : this.config.text_valign === 'bottom'
                        ? 'flex-end'
                        : 'center',
                justifyContent:
                    this.config.text_halign === 'left'
                        ? 'flex-start'
                        : this.config.text_halign === 'right'
                        ? 'flex-end'
                        : 'center',
                backgroundImage:
                    this.config && this.config.image_url ? `url(${this.config.image_url})` : null,
                backgroundPosition: this.config ? `${this.config.image_halign} ${this.config.image_valign}` : 'center',
                backgroundSize: this.config
                    ? this.config.image_stretch
                        ? 'cover'
                        : this.config.image_fit
                        ? 'contain'
                        : '50%'
                    : null
            };
        }
    },

    methods: {
        menuItemClick(item, data = null) {
            if (item === 'edit') {
                this.mode = 'edit';
                this.$nextTick(() => {
                    this.$refs.editNameField.focus();
                    this.$refs.editNameField.select();
                    this.showMenu = false;
                });
            } else if (item === 'color') {
                this.config.background_color = data;
                this.$emit('config-update', this.config);
            } else if (item === 'delete') {
                this.$emit('delete', this.config);
            }
        },

        /**
         * Finish text edit
         */
        finishEdit() {
            this.mode = null;
            this.$emit('config-update', this.config);
        },

        /**
         * Align text
         */
        alignText(alignment) {
            this.config.text_halign = alignment.horizontal;
            this.config.text_valign = alignment.vertical;
            this.$emit('config-update', this.config);
        },

        /**
         * Open select image dialog
         */
        openImageDialog() {
            this.imageDialogVisible = true;
            this.newImage = null;
            this.showImageSettingsMenu = false;
        },

        /**
         * Update the image
         */
        updateImage() {
            this.imageDialogVisible = false;
            this.config.image = this.newImage ? this.newImage.path : null;
            this.config.image_url = this.newImage ? this.newImage.image_url : null;
            this.$emit('config-update', this.config);
        },

        /**
         * Fit image into the button container
         */
        fitImage() {
            this.config.image_fit = !this.config.image_fit;
            if (this.config.image_fit) {
                this.config.image_stretch = false;
            }

            this.$emit('config-update', this.config);
        },

        /**
         * Stretch image over the button container
         */
        stretchImage() {
            this.config.image_stretch = !this.config.image_stretch;
            if (this.config.image_stretch) {
                this.config.image_fit = false;
            }
            this.$emit('config-update', this.config);
        },

        /**
         * Align image
         */
        alignImage(alignment) {
            this.config.image_halign = alignment.horizontal;
            this.config.image_valign = alignment.vertical;
            this.$emit('config-update', this.config);
        },

        /**
         * Upload Button Image
         */
        uploadImage(event) {
            let files = event.target.files || event.dataTransfer.files;
            if (!files.length) return;

            let data = { name: event.target.files[0].name, file: null };

            var reader = new FileReader();
            reader.onload = e => {
                data.file = e.target.result;
                ApiService.post('/admin/buttons/images', data).then(response => {
                    this.images.unshift(response.data);
                });
            };
            reader.readAsDataURL(files[0]);
        },

        /**
         * Lighten or darken hexadecimal color
         *
         * @param color
         * @param percent
         * @returns {string}
         */
        shadeColor(color, percent) {
            var R = parseInt(color.substring(1, 3), 16);
            var G = parseInt(color.substring(3, 5), 16);
            var B = parseInt(color.substring(5, 7), 16);

            R = parseInt((R * (100 + percent)) / 100);
            G = parseInt((G * (100 + percent)) / 100);
            B = parseInt((B * (100 + percent)) / 100);

            R = R < 255 ? R : 255;
            G = G < 255 ? G : 255;
            B = B < 255 ? B : 255;

            var RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
            var GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
            var BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

            return '#' + RR + GG + BB;
        }
    }
};
</script>

<style lang="scss">
.sales-button {
    position: relative;
    width: 100%;
    height: 100%;

    .button-element {
        width: 100%;
        min-width: 0 !important;
        height: 100% !important;
        margin: 0;
        min-width: 0;
        white-space: normal;
        word-break: break-word;

        .v-btn__content {
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            padding: 5px;

            .text-wrapper {
                height: 100%;
                width: 100%;
                display: flex;
                text-transform: none;
                position: relative;

                .v-badge {
                    position: absolute;
                    top: 15px;
                    right: 15px;
                }
            }
        }
    }

    &.draggable {
        .sales-button {
            cursor: inherit;
        }
    }

    .menu {
        transition: visibility 0s, opacity 0.2s linear;
        background: #2f404f;
        border-radius: 3px;
        padding: 4px;
        position: absolute;
        bottom: -30px;
        left: -20px;
        right: -20px;
        margin: 0 auto;
        width: 135px;
        cursor: pointer;
        z-index: 10000;

        .label {
            color: white;
            width: 100%;
            text-align: center;
        }

        .menu-item {
            margin: 0px;
            height: 25px;
            width: 25px;
            min-width: 0;

            &:hover {
                color: #ffd04b;
            }
        }
    }

    .edit-name {
        background: none;
        border: none;
        text-align: center;
        outline: none;
        border-bottom: 1px solid gray;
        width: 100%;
    }
}

.sales-button__text-settings {
    background: white;
    border: none;
    padding: 20px;

    .label {
        margin-bottom: 10px;
        font-weight: bold;
    }

    .align {
        display: flex;
        justify-content: center;

        .text-align {
            height: 68px;
            width: 68px;
        }
    }
}

.sales-button__image-settings {
    background: white;
    border: none;
    padding: 20px;

    .label {
        margin-bottom: 10px;
        font-weight: bold;
    }

    .align {
        display: flex;

        .fit-stretch {
            display: block;
            margin-right: 10px;
            flex-direction: column;
            display: flex;

            .v-btn {
                margin: 5px 0px;
                width: 70px;
                display: block;
            }
        }

        .image-align {
            margin: 5px 0px;
            height: 68px;
            width: 68px;
        }
    }

    .select-button {
        margin-top: 15px;
    }
}

.sales-button__select-color {
    border: none;
    width: 100px;
    margin: 0;
    padding: 0;

    & * {
        padding: 0;
        margin: 0;
        height: 30px;
    }

    .v-list-item {
        min-height: 0;

        &:hover {
            opacity: 0.9;
        }
    }
}

.sales-button__button-image-dialog {
    background: white;

    .header {
        padding: 10px;
        background: lightgrey;
    }

    .content {
        background: #f9f9f9;
        padding: 10px;
        display: flex;
        flex-wrap: wrap;
        max-height: 320px;
        overflow-y: auto;

        .upload-btn {
            min-width: 0;
            width: 120px;
            height: 80px;
            border: 1px dashed lightgrey;
            font-size: 24px;
            margin: 5px;
        }

        .image-wrapper {
            width: 120px;
            height: 80px;
            background: white;
            border-radius: 4px;
            border: 1px solid #dcdfe6;
            margin: 5px;

            .image {
                width: 100%;
                height: 100%;
            }

            &:hover {
                border: 3px solid rgba(64, 158, 255, 0.54);
                cursor: pointer;
            }

            &.selected {
                border: 3px solid #409eff;
            }
        }
    }

    .footer {
        padding: 10px;
        display: flex;
    }
}
</style>
